import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useUI } from '../contexts/UIContext'
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
interface Cliente { id: string; nome_fantasia: string; razao_social: string }
interface MarcaModelo { marca: string; modelo: string }
interface AtivoComando {
  id: string; nome: string; placa: string | null; imei: string
  marca: string | null; modelo: string | null; cliente_nome: string | null
}
interface ComandoCatalogoItem {
  id: string; label: string; categoria: 'acao' | 'consulta' | 'configuracao' | 'gs404'
  exigeValor: boolean; labelValor: string | null
}
interface ComandoPersonalizado {
  id: string; nome: string; descricao: string | null; comando: string
  cliente_id: string | null; cliente_nome: string | null
}
interface PreviewItem {
  ativo_id: string; ativo_nome: string; imei: string; comando_final: string; erro?: string
}
const LABEL_CATEGORIA: Record<string, string> = {
  acao: 'Ações', consulta: 'Consultas', configuracao: 'Configuração', gs404: 'GS404 (identificador RFID)',
}
// Estilo padrão de checkbox reutilizado nos dois pontos da tela (seleção
// "todos" e itens individuais) — accentColor casa com o tema escuro e
// remove qualquer margem/alinhamento herdado do navegador.
const estiloCheckbox: React.CSSProperties = {
  flexShrink: 0, margin: 0, width: 14, height: 14, accentColor: '#0ea5e9', cursor: 'pointer', verticalAlign: 'middle',
}
export default function Comandos() {
  const { toast } = useUI()
  const [aba, setAba] = useState<'envio' | 'historico'>('envio')
  // Filtros de seleção de ativos
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [marcasModelos, setMarcasModelos] = useState<MarcaModelo[]>([])
  const [clienteFiltro, setClienteFiltro] = useState('')
  const [modeloFiltro, setModeloFiltro] = useState('')
  const [busca, setBusca] = useState('')
  // Lista de ativos e seleção
  const [ativos, setAtivos] = useState<AtivoComando[]>([])
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set())
  const [carregandoAtivos, setCarregandoAtivos] = useState(true)
  // Fonte do comando: catálogo fixo, personalizado salvo, ou texto livre digitado na hora
  const [fonte, setFonte] = useState<'catalogo' | 'personalizado' | 'livre'>('catalogo')
  const [catalogo, setCatalogo] = useState<ComandoCatalogoItem[]>([])
  const [comandoId, setComandoId] = useState('')
  const [valorExtra, setValorExtra] = useState('')
  const [comandosPersonalizados, setComandosPersonalizados] = useState<ComandoPersonalizado[]>([])
  const [comandoPersonalizadoId, setComandoPersonalizadoId] = useState('')
  const [comandoTextoLivre, setComandoTextoLivre] = useState('')
  // Prévia e envio
  const [preview, setPreview] = useState<PreviewItem[]>([])
  const [carregandoPreview, setCarregandoPreview] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [resultadoEnvio, setResultadoEnvio] = useState<any[] | null>(null)
  const [modalCadastroAberto, setModalCadastroAberto] = useState(false)
  const token = localStorage.getItem('moobi_token')
  const headers = { Authorization: `Bearer ${token}` }

  // Carrega filtros (clientes uma vez, comandos personalizados) e recarrega
  // marcas/modelos sempre que o cliente selecionado muda — restringindo aos
  // modelos efetivamente instalados naquele cliente. Se sobrar só um modelo,
  // seleciona automaticamente (poupa um clique no caso comum).
  useEffect(() => {
    axios.get(`${API}/clientes`, { headers }).then((r) => setClientes(r.data)).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = clienteFiltro ? { cliente_id: clienteFiltro } : {}
    axios.get(`${API}/comandos-em-massa/marcas-modelos`, { headers, params })
      .then((r) => {
        setMarcasModelos(r.data)
        if (clienteFiltro && r.data.length === 1) {
          setModeloFiltro(`${r.data[0].marca}|${r.data[0].modelo}`)
        } else {
          setModeloFiltro('')
        }
        setComandoId(''); setValorExtra('')
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteFiltro])

  const carregarComandosPersonalizados = useCallback(() => {
    const params = clienteFiltro ? { cliente_id: clienteFiltro } : {}
    axios.get(`${API}/comandos-personalizados`, { headers, params }).then((r) => setComandosPersonalizados(r.data)).catch(() => {})
  }, [clienteFiltro])

  useEffect(() => { carregarComandosPersonalizados() }, [carregarComandosPersonalizados])

  // Carrega ativos sempre que algum filtro muda
  const carregarAtivos = useCallback(async () => {
    setCarregandoAtivos(true)
    try {
      const [marca, modelo] = modeloFiltro ? modeloFiltro.split('|') : [undefined, undefined]
      const params: any = {}
      if (clienteFiltro) params.cliente_id = clienteFiltro
      if (marca) params.marca = marca
      if (modelo) params.modelo = modelo
      if (busca) params.busca = busca
      const r = await axios.get(`${API}/comandos-em-massa/ativos`, { headers, params })
      setAtivos(r.data)
    } catch {
      toast('Erro ao carregar ativos', 'error')
    } finally {
      setCarregandoAtivos(false)
    }
  }, [clienteFiltro, modeloFiltro, busca])

  useEffect(() => { carregarAtivos() }, [carregarAtivos])

  // Carrega o catálogo de comandos quando o filtro de modelo muda (o
  // catálogo é específico por marca/modelo Suntech — sem modelo selecionado
  // ainda não sabemos qual catálogo mostrar).
  useEffect(() => {
    if (!modeloFiltro) { setCatalogo([]); return }
    const [marca, modelo] = modeloFiltro.split('|')
    const m = modelo.toUpperCase().replace(/\s/g, '')
    const fab = marca.toUpperCase()
    let modeloCatalogo: string | null = null
    if (fab === 'SUNTECH') {
      if (m.includes('8300')) modeloCatalogo = 'suntech_st8300'
      else if (m.includes('340')) modeloCatalogo = 'suntech_st340us'
    }
    if (!modeloCatalogo) { setCatalogo([]); setComandoId(''); return }
    // Usa o primeiro ativo do filtro atual só para descobrir o catálogo —
    // o endpoint por-ativo já existente devolve o catálogo certo.
    axios.get(`${API}/comandos-em-massa/ativos`, { headers, params: { marca, modelo } })
      .then(async (r) => {
        const primeiroAtivo = r.data[0]
        if (!primeiroAtivo) { setCatalogo([]); return }
        const catR = await axios.get(`${API}/ativos/${primeiroAtivo.id}/comandos/catalogo`, { headers })
        setCatalogo(catR.data.comandos)
      })
      .catch(() => setCatalogo([]))
  }, [modeloFiltro])

  const toggleSelecionado = (id: string) => {
    setSelecionados((atual) => {
      const novo = new Set(atual)
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })
  }

  const toggleTodos = () => {
    if (selecionados.size === ativos.length) setSelecionados(new Set())
    else setSelecionados(new Set(ativos.map((a) => a.id)))
  }

  const comandoCatalogoAtual = catalogo.find((c) => c.id === comandoId)
  const comandoPersonalizadoAtual = comandosPersonalizados.find((c) => c.id === comandoPersonalizadoId)

  const podeVerPreview =
    selecionados.size > 0 &&
    (fonte === 'catalogo' ? !!comandoId && (!comandoCatalogoAtual?.exigeValor || !!valorExtra)
      : fonte === 'personalizado' ? !!comandoPersonalizadoId
      : !!comandoTextoLivre.trim())

  // Recalcula a prévia sempre que a seleção ou o comando escolhido muda
  useEffect(() => {
    if (!podeVerPreview) { setPreview([]); return }
    setCarregandoPreview(true)
    const body: any = { ativo_ids: Array.from(selecionados), fonte }
    if (fonte === 'catalogo') { body.comando_id = comandoId; body.valor = valorExtra }
    else if (fonte === 'personalizado') { body.comando_personalizado_id = comandoPersonalizadoId }
    else { body.comando_texto = comandoTextoLivre }

    axios.post(`${API}/comandos-em-massa/preview`, body, { headers })
      .then((r) => setPreview(r.data))
      .catch(() => toast('Erro ao gerar prévia do comando', 'error'))
      .finally(() => setCarregandoPreview(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVerPreview, fonte, comandoId, valorExtra, comandoPersonalizadoId, comandoTextoLivre, selecionados])

  const enviarComandos = async () => {
    if (!podeVerPreview) return
    setEnviando(true)
    setResultadoEnvio(null)
    try {
      const body: any = { ativo_ids: Array.from(selecionados), fonte }
      if (fonte === 'catalogo') { body.comando_id = comandoId; body.valor = valorExtra }
      else if (fonte === 'personalizado') { body.comando_personalizado_id = comandoPersonalizadoId }
      else { body.comando_texto = comandoTextoLivre }
      const r = await axios.post(`${API}/comandos-em-massa/enviar`, body, { headers })
      setResultadoEnvio(r.data)
      const sucessos = r.data.filter((i: any) => i.sucesso).length
      toast(`Comando enviado para ${sucessos} de ${r.data.length} ativos`, sucessos === r.data.length ? 'success' : 'warning')
    } catch {
      toast('Erro ao enviar comandos', 'error')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
          <i className="ph-bold ph-broadcast" style={{ marginRight: 8, color: '#0ea5e9' }} />
          Envio de comandos
        </h1>
        <button onClick={() => setModalCadastroAberto(true)}
          style={{ background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#94a3b8',
            padding: '8px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="ph-bold ph-plus" style={{ fontSize: 14 }} />
          Comandos personalizados
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setAba('envio')}
          style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
            background: aba === 'envio' ? '#0c2a4a' : 'transparent',
            border: `1px solid ${aba === 'envio' ? '#38bdf8' : '#2d4166'}`,
            color: aba === 'envio' ? '#38bdf8' : '#94a3b8' }}>
          <i className="ph-bold ph-paper-plane-tilt" style={{ marginRight: 6 }} />Enviar
        </button>
        <button onClick={() => setAba('historico')}
          style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
            background: aba === 'historico' ? '#0c2a4a' : 'transparent',
            border: `1px solid ${aba === 'historico' ? '#38bdf8' : '#2d4166'}`,
            color: aba === 'historico' ? '#38bdf8' : '#94a3b8' }}>
          <i className="ph-bold ph-clock-counter-clockwise" style={{ marginRight: 6 }} />Histórico
        </button>
      </div>

      {aba === 'historico' ? <HistoricoComandos clientes={clientes} /> : <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Cliente</label>
          <select value={clienteFiltro} onChange={(e) => setClienteFiltro(e.target.value)}
            style={{ width: '100%', background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }}>
            <option value="">Todos os clientes</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Modelo de rastreador</label>
          <select value={modeloFiltro} onChange={(e) => { setModeloFiltro(e.target.value); setComandoId(''); setValorExtra('') }}
            style={{ width: '100%', background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }}>
            <option value="">Todos os modelos</option>
            {marcasModelos.map((mm) => (
              <option key={`${mm.marca}|${mm.modelo}`} value={`${mm.marca}|${mm.modelo}`}>{mm.marca} {mm.modelo}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Pesquisar por nome ou placa..."
          style={{ width: '100%', background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }} />
      </div>

      {/* Lista de ativos */}
      <div style={{ border: '1px solid #2d4166', borderRadius: 10, overflow: 'hidden', marginBottom: 16, maxHeight: 360, overflowY: 'auto' }}>
        <div onClick={toggleTodos} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 12px',
          background: '#1e2f4a', borderBottom: '1px solid #2d4166', position: 'sticky', top: 0, cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6, fontSize: 12, color: '#cbd5e1' }}>
            <input type="checkbox" readOnly checked={ativos.length > 0 && selecionados.size === ativos.length}
              style={estiloCheckbox} />
            <span>Selecionar todos ({ativos.length})</span>
          </div>
          <span style={{ fontSize: 11, color: '#64748b' }}>{selecionados.size} de {ativos.length} selecionados</span>
        </div>
        {carregandoAtivos ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 12 }}>Carregando...</div>
        ) : ativos.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 12 }}>Nenhum ativo encontrado com esses filtros</div>
        ) : (
          ativos.map((a) => (
            <div key={a.id} onClick={() => toggleSelecionado(a.id)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6, padding: '5px 12px',
                borderBottom: '1px solid #162035', cursor: 'pointer', textAlign: 'left' }}>
              <input type="checkbox" readOnly checked={selecionados.has(a.id)}
                style={estiloCheckbox} />
              <i className="ph-bold ph-car" style={{ fontSize: 14, color: '#64748b', flexShrink: 0, width: 14, textAlign: 'center' }} />
              <span style={{ fontSize: 12, color: '#f1f5f9', whiteSpace: 'nowrap', flexShrink: 0 }}>{a.nome}{a.placa ? ` · ${a.placa}` : ''}</span>
              <span style={{ fontSize: 10, color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                IMEI {a.imei} · {a.marca} {a.modelo}{a.cliente_nome ? ` · ${a.cliente_nome}` : ''}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Fonte do comando */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setFonte('catalogo')}
          style={{ flex: 1, padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13,
            background: fonte === 'catalogo' ? '#0c2a4a' : '#1e2f4a',
            border: `1px solid ${fonte === 'catalogo' ? '#38bdf8' : '#2d4166'}`,
            color: fonte === 'catalogo' ? '#38bdf8' : '#94a3b8' }}>
          Comando pré-configurado
        </button>
        <button onClick={() => setFonte('personalizado')}
          style={{ flex: 1, padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13,
            background: fonte === 'personalizado' ? '#0c2a4a' : '#1e2f4a',
            border: `1px solid ${fonte === 'personalizado' ? '#38bdf8' : '#2d4166'}`,
            color: fonte === 'personalizado' ? '#38bdf8' : '#94a3b8' }}>
          Comando personalizado
        </button>
        <button onClick={() => setFonte('livre')}
          style={{ flex: 1, padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13,
            background: fonte === 'livre' ? '#0c2a4a' : '#1e2f4a',
            border: `1px solid ${fonte === 'livre' ? '#38bdf8' : '#2d4166'}`,
            color: fonte === 'livre' ? '#38bdf8' : '#94a3b8' }}>
          Comando livre
        </button>
      </div>

      {/* Seleção do comando */}
      {fonte === 'catalogo' ? (
        !modeloFiltro ? (
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, background: '#1e2f4a', borderRadius: 8, padding: 12 }}>
            <i className="ph-bold ph-info" style={{ marginRight: 6 }} />
            Selecione um modelo de rastreador no filtro acima para ver o catálogo de comandos disponível.
          </div>
        ) : catalogo.length === 0 ? (
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, background: '#1e2f4a', borderRadius: 8, padding: 12 }}>
            Esse modelo não tem catálogo de comandos disponível ainda.
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Comando</label>
            <select value={comandoId} onChange={(e) => { setComandoId(e.target.value); setValorExtra('') }}
              style={{ width: '100%', background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }}>
              <option value="">Selecione um comando...</option>
              {['acao', 'consulta', 'configuracao', 'gs404'].map((cat) => {
                const itens = catalogo.filter((c) => c.categoria === cat)
                if (itens.length === 0) return null
                return (
                  <optgroup key={cat} label={LABEL_CATEGORIA[cat]}>
                    {itens.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </optgroup>
                )
              })}
            </select>
            {comandoCatalogoAtual?.exigeValor && (
              <input value={valorExtra} onChange={(e) => setValorExtra(e.target.value)}
                placeholder={comandoCatalogoAtual.labelValor || 'Valor'}
                style={{ width: '100%', marginTop: 8, background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }} />
            )}
          </div>
        )
      ) : fonte === 'personalizado' ? (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Comando personalizado</label>
          <select value={comandoPersonalizadoId} onChange={(e) => setComandoPersonalizadoId(e.target.value)}
            style={{ width: '100%', background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }}>
            <option value="">Selecione um comando...</option>
            {comandosPersonalizados.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}{c.cliente_nome ? ` (${c.cliente_nome})` : ' (genérico)'}</option>
            ))}
          </select>
          {comandoPersonalizadoAtual?.descricao && (
            <p style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>{comandoPersonalizadoAtual.descricao}</p>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Comando livre</label>
          <textarea value={comandoTextoLivre} onChange={(e) => setComandoTextoLivre(e.target.value)}
            placeholder="Ex: PRG;{imei};16;70#30;79#30"
            rows={2}
            style={{ width: '100%', background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9',
              padding: 8, fontFamily: 'monospace', fontSize: 13, resize: 'vertical' }} />
          <p style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
            Use <code style={{ color: '#38bdf8' }}>{'{imei}'}</code> no texto — será substituído pelo IMEI de cada ativo selecionado ao enviar. Este comando não fica salvo; para reutilizar, cadastre em "Comandos personalizados".
          </p>
        </div>
      )}

      {/* Prévia */}
      {(carregandoPreview || preview.length > 0) && (
        <div style={{ background: '#1e2f4a', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12,
          fontFamily: 'monospace', color: '#94a3b8', maxHeight: 160, overflowY: 'auto' }}>
          {carregandoPreview ? 'Calculando prévia...' : preview.map((item) => (
            <div key={item.ativo_id} style={{ marginBottom: 4, color: item.erro ? '#f87171' : '#94a3b8' }}>
              {item.ativo_nome} → {item.erro || item.comando_final}
            </div>
          ))}
        </div>
      )}

      {/* Resultado do envio */}
      {resultadoEnvio && (
        <div style={{ marginBottom: 16 }}>
          {resultadoEnvio.map((item) => (
            <div key={item.ativo_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
              background: '#1e2f4a', borderRadius: 8, marginBottom: 4, fontSize: 12 }}>
              <i className={`ph-bold ${item.sucesso ? 'ph-check-circle' : 'ph-x-circle'}`}
                style={{ color: item.sucesso ? '#4ade80' : '#f87171' }} />
              <span style={{ color: '#f1f5f9', flex: 1 }}>{item.ativo_nome}</span>
              <span style={{ color: item.sucesso ? '#4ade80' : '#f87171' }}>{item.mensagem || item.erro}</span>
            </div>
          ))}
        </div>
      )}

      <button onClick={enviarComandos} disabled={!podeVerPreview || enviando}
        style={{ width: '100%', padding: 12, borderRadius: 8, border: 'none', cursor: podeVerPreview ? 'pointer' : 'not-allowed',
          background: podeVerPreview ? '#0ea5e9' : '#1e2f4a', color: podeVerPreview ? '#fff' : '#475569',
          fontSize: 14, fontWeight: 700 }}>
        {enviando ? 'Enviando...' : `Enviar para ${selecionados.size} ${selecionados.size === 1 ? 'ativo' : 'ativos'}`}
      </button>
      </>}

      {modalCadastroAberto && (
        <ModalComandosPersonalizados
          clientes={clientes}
          comandos={comandosPersonalizados}
          onFechar={() => { setModalCadastroAberto(false); carregarComandosPersonalizados() }}
        />
      )}
    </div>
  )
}

// ───────────────────────────────────────────────────────────
// Aba de Histórico: lista comandos já enviados (comandos_rastreador) com
// filtros por ativo e período — "revisão manual" do que foi disparado.
// ───────────────────────────────────────────────────────────
function HistoricoComandos({ clientes }: { clientes: Cliente[] }) {
  const [lista, setLista] = useState<any[]>([])
  const [carregando, setCarregando] = useState(true)
  const [ativoFiltro, setAtivoFiltro] = useState('')
  const [ativosBusca, setAtivosBusca] = useState<AtivoComando[]>([])

  const token = localStorage.getItem('moobi_token')
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    axios.get(`${API}/comandos-em-massa/ativos`, { headers }).then((r) => setAtivosBusca(r.data)).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const carregar = useCallback(() => {
    setCarregando(true)
    const params: any = {}
    if (ativoFiltro) params.ativo_id = ativoFiltro
    axios.get(`${API}/comandos-em-massa/historico`, { headers, params })
      .then((r) => setLista(r.data))
      .catch(() => {})
      .finally(() => setCarregando(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativoFiltro])

  useEffect(() => { carregar() }, [carregar])

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Filtrar por ativo</label>
        <select value={ativoFiltro} onChange={(e) => setAtivoFiltro(e.target.value)}
          style={{ width: '100%', background: '#1e2f4a', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }}>
          <option value="">Todos os ativos</option>
          {ativosBusca.map((a) => <option key={a.id} value={a.id}>{a.nome}{a.placa ? ` · ${a.placa}` : ''}</option>)}
        </select>
      </div>

      {carregando ? (
        <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12, padding: '30px 0' }}>Carregando...</div>
      ) : lista.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12, padding: '30px 0' }}>
          <i className="ph-bold ph-tray" style={{ fontSize: 24, display: 'block', marginBottom: 6 }} />
          Nenhum comando encontrado
        </div>
      ) : (
        <div style={{ border: '1px solid #2d4166', borderRadius: 10, overflow: 'hidden' }}>
          {lista.map((c) => (
            <div key={c.id} style={{ padding: '10px 14px', borderBottom: '1px solid #162035' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>
                  {c.ativo_nome}{c.placa ? ` · ${c.placa}` : ''}
                </span>
                <span style={{ fontSize: 11, color: '#64748b' }}>{new Date(c.criado_em).toLocaleString('pt-BR')}</span>
              </div>
              <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#94a3b8', marginBottom: 4 }}>{c.comando}</div>
              <div style={{ display: 'flex', gap: 10, fontSize: 10, color: '#64748b' }}>
                <span>Por: {c.usuario_nome || '—'}</span>
                <span style={{ color: c.status === 'enviado' ? '#38bdf8' : '#94a3b8' }}>Status: {c.status}</span>
                {c.resposta && <span>Resposta: {c.resposta}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ───────────────────────────────────────────────────────────
// Modal de cadastro/gestão de comandos personalizados
// ───────────────────────────────────────────────────────────
function ModalComandosPersonalizados({ clientes, comandos, onFechar }: {
  clientes: Cliente[]; comandos: ComandoPersonalizado[]; onFechar: () => void
}) {
  const { toast } = useUI()
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [comandoTexto, setComandoTexto] = useState('')
  const [clienteId, setClienteId] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [lista, setLista] = useState<ComandoPersonalizado[]>(comandos)

  const token = localStorage.getItem('moobi_token')
  const headers = { Authorization: `Bearer ${token}` }

  const recarregar = async () => {
    const r = await axios.get(`${API}/comandos-personalizados`, { headers })
    setLista(r.data)
  }

  const salvar = async () => {
    if (!nome || !comandoTexto) return toast('Nome e comando são obrigatórios', 'error')
    setSalvando(true)
    try {
      await axios.post(`${API}/comandos-personalizados`, {
        nome, descricao: descricao || null, comando: comandoTexto, cliente_id: clienteId || null,
      }, { headers })
      toast('Comando personalizado criado', 'success')
      setNome(''); setDescricao(''); setComandoTexto(''); setClienteId('')
      await recarregar()
    } catch {
      toast('Erro ao criar comando personalizado', 'error')
    } finally {
      setSalvando(false)
    }
  }

  const remover = async (id: string) => {
    try {
      await axios.delete(`${API}/comandos-personalizados/${id}`, { headers })
      toast('Comando removido', 'success')
      await recarregar()
    } catch {
      toast('Erro ao remover comando', 'error')
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#162035', borderRadius: 16, padding: 20, width: '100%', maxWidth: 560,
        maxHeight: '85vh', overflowY: 'auto', border: '1px solid #2d4166' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Comandos personalizados</h2>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>
            <i className="ph-bold ph-x" />
          </button>
        </div>

        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Use <code style={{ color: '#38bdf8' }}>{'{imei}'}</code> no texto do comando — será substituído pelo IMEI de cada ativo ao enviar. Útil para cercas embarcadas e parâmetros específicos de cliente.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, background: '#1e2f4a', borderRadius: 8, padding: 12 }}>
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do comando"
            style={{ background: '#0d1b2e', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }} />
          <input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Descrição (opcional)"
            style={{ background: '#0d1b2e', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }} />
          <input value={comandoTexto} onChange={(e) => setComandoTexto(e.target.value)} placeholder="Ex: PRG;{imei};16;70#30;79#30"
            style={{ background: '#0d1b2e', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8, fontFamily: 'monospace' }} />
          <select value={clienteId} onChange={(e) => setClienteId(e.target.value)}
            style={{ background: '#0d1b2e', border: '1px solid #2d4166', borderRadius: 8, color: '#f1f5f9', padding: 8 }}>
            <option value="">Genérico (todos os clientes)</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
          <button onClick={salvar} disabled={salvando}
            style={{ background: '#0ea5e9', border: 'none', borderRadius: 8, color: '#fff', padding: 10, cursor: 'pointer', fontWeight: 700 }}>
            {salvando ? 'Salvando...' : 'Adicionar comando'}
          </button>
        </div>

        <div>
          {lista.length === 0 ? (
            <p style={{ fontSize: 12, color: '#64748b', textAlign: 'center', padding: 16 }}>Nenhum comando cadastrado ainda.</p>
          ) : (
            lista.map((c) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                background: '#1e2f4a', borderRadius: 8, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#f1f5f9' }}>
                    {c.nome}
                    <span style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>
                      {c.cliente_nome ? `(${c.cliente_nome})` : '(genérico)'}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: '#475569', fontFamily: 'monospace' }}>{c.comando}</div>
                </div>
                <button onClick={() => remover(c.id)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer' }}>
                  <i className="ph-bold ph-trash" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
